import HeadMetadata from 'components/shared/HeadMetadata';
import { useI18n } from 'hooks/useI18n';
import { usePageTrack } from 'hooks/usePageTrack';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Script from 'next/script';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { getPublicPath } from 'utils/paths';
import { avoidRateLimit } from 'utils/rate-limit';
import { WP_REST_API_Categories, WP_REST_API_Posts } from 'wp-types';
import BlogComponent from '../../../components/marketingSite/blog/BlogComponent';

type BlogPageProps = {
  posts?: WP_REST_API_Posts;
  categories?: WP_REST_API_Categories;
  totalPosts: number;
  totalPages: number;
  currentPage: number;
};

const BlogPage: React.FC<BlogPageProps> = (props) => {
  const { t } = useI18n('blog');

  usePageTrack('Blog - Page Loaded');

  return (
    <>
      <HeadMetadata
        title={t('pageTitle')}
        desc={t('pageDescription')}
        imgUrl={getPublicPath('/images/card_blog.png')}
        url={APP_URLS.blog}
        path={{
          ES: `page/${props.currentPage}`,
          EN: `page/${props.currentPage}`
        }}
        indexable={true}
      />
      <Script
        id="blogMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />
      <BlogComponent {...props} />
    </>
  );
};

export default BlogPage;

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}

export async function getStaticProps({
  locale,
  params
}: {
  params: {
    page: string;
  };
  locale: string;
}) {
  await avoidRateLimit();
  const responsePosts = await WPService.getPosts({
    page: !isNaN(Number(params.page)) ? Number(params.page) : undefined,
    locale: locale || 'en'
  });
  await avoidRateLimit();
  const categories = await WPService.getCategories(locale || 'en');

  if (!responsePosts?.data || !Array.isArray(responsePosts?.data)) {
    return {
      notFound: true,
      revalidate: 10
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'topbar',
        'blog',
        'footer'
      ])),
      posts: responsePosts?.data,
      totalPosts: Number(responsePosts?.headers.get('x-wp-total') || ''),
      totalPages: Number(responsePosts?.headers.get('x-wp-totalpages') || ''),
      currentPage: Number(params.page),
      categories
    },
    revalidate: 10
  };
}
